import type { Middleware } from 'openapi-fetch'

/** The only two content-types this app's API contract emits for a body we
 * expect openapi-fetch to `JSON.parse()` — a normal success payload, or the
 * problem+json shape the API already uses for structured errors (see
 * `schema.gen.ts` `components["schemas"]["Problem"]`). */
const JSON_MIME_TYPES = new Set(['application/json', 'application/problem+json'])

function isJsonMimeType(contentType: string | null): boolean {
  if (!contentType) return false
  // Strip a trailing `; charset=...` etc. before comparing the bare mime type.
  const mime = contentType.split(';')[0]?.trim().toLowerCase()
  return mime !== undefined && JSON_MIME_TYPES.has(mime)
}

/**
 * Guards every `apiClient` call (installed once in `client.ts` — the ONLY
 * client apps/web makes API calls through, per F0.2) against a non-JSON
 * response body reaching openapi-fetch's own `response.json()` /
 * `JSON.parse()` calls. Left alone, those throw an unintelligible
 * `Unexpected token '<', "<!doctype "... is not valid JSON` with no status
 * code and no endpoint name whenever the API or an intermediary (e.g. an
 * nginx 502/504 error page, or a misconfigured proxy serving the SPA's own
 * `index.html` fallback) returns HTML instead of the expected JSON.
 *
 * Detection is by `content-type` header — the contract — never by sniffing
 * the body for a leading `<`, which is a heuristic that would misfire on
 * legitimate bodies that merely look unusual. A genuine `application/json`
 * or `application/problem+json` response, including real 4xx/5xx
 * problem+json errors (e.g. a 409 from `confirm-parts`), passes through this
 * middleware completely untouched — this never reads or alters that body.
 *
 * 204/HEAD/`Content-Length: 0` responses are left alone too: there is no
 * body to inspect, and openapi-fetch's own core already special-cases these
 * as empty without ever calling `.json()` on them.
 *
 * When the body IS non-JSON, this synthesizes an `application/problem+json`
 * body so every existing call site that reads `error.status`/`error.detail`/
 * `error.title` keeps working unchanged. The real HTTP status is preserved
 * whenever the upstream response was already an error (e.g. 502 stays 502 —
 * never normalised to 500); a response that claims success (2xx) but
 * carries a non-JSON body is itself the upstream misconfiguration, so it is
 * re-flagged as a 502 rather than allowed to look like a successful call
 * with a garbage payload.
 *
 * Logs once, at this boundary, with only the method/path/status (never the
 * response body, which may carry customer data) so the next occurrence is
 * diagnosable from the console without a network-tab excavation.
 */
export const nonJsonResponseMiddleware: Middleware = {
  async onResponse({ request, response, schemaPath }) {
    if (
      response.status === 204 ||
      request.method === 'HEAD' ||
      response.headers.get('content-length') === '0'
    ) {
      return undefined
    }

    if (isJsonMimeType(response.headers.get('content-type'))) {
      return undefined
    }

    const originalStatus = response.status
    const wasAlreadyErrorStatus = originalStatus < 200 || originalStatus >= 300
    const outgoingStatus = wasAlreadyErrorStatus ? originalStatus : 502

    // eslint-disable-next-line no-console -- intentional: the ONE diagnostic
    // log at the API boundary; status/method/path only, no body.
    console.error(
      `[apiClient] non-JSON response for ${request.method} ${schemaPath}: status ${originalStatus}${
        wasAlreadyErrorStatus ? '' : ` (reporting as ${outgoingStatus})`
      }`,
    )

    const detail = wasAlreadyErrorStatus
      ? `The API returned a non-JSON response (status ${originalStatus}) for ${request.method} ${schemaPath}. The API or an upstream gateway may be unreachable or misconfigured.`
      : `The API returned a non-JSON response with an unexpected success status (${originalStatus}) for ${request.method} ${schemaPath}. Treating this as a gateway failure (${outgoingStatus}) rather than a successful call.`

    const problem = {
      type: 'about:blank',
      title: 'Unexpected API response',
      status: outgoingStatus,
      detail,
      instance: schemaPath,
    }

    return new Response(JSON.stringify(problem), {
      status: outgoingStatus,
      statusText: response.statusText,
      headers: { 'content-type': 'application/problem+json' },
    })
  },
}
